import { isAlphaNumericKey, keyCodes } from '@silevis/reactgrid';
import moment from 'moment';
import { getPnlConfigForInventory } from '../components/inventory/inventoryPnlHelpers';
import { APP_URL, isMicasa } from './constants';

export const downloadFileFromString = ({ data, fileName }) => {
  var downloadLink = document.createElement('a');
  var blob = new Blob(['\ufeff', data]);
  var url = URL.createObjectURL(blob);
  downloadLink.href = url;
  downloadLink.download = fileName;

  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};

export const downloadFileFromBase64 = ({ data, fileName, mimeType }) => {
  const triggerDownload = url => {
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = fileName || 'download';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  try {
    if (typeof data === 'string' && /^(blob:|https?:)/i.test(data)) {
      triggerDownload(data);
      return true;
    }

    if (typeof data !== 'string' || !data) {
      return false;
    }

    const base64 = (data.startsWith('data:') ? data.slice(data.indexOf(',') + 1) : data).replace(
      /\s/g,
      ''
    );
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const type =
      mimeType ||
      (data.startsWith('data:') ? data.slice(5, data.indexOf(';')) : 'application/octet-stream');
    const objectUrl = URL.createObjectURL(new Blob([bytes], { type }));
    triggerDownload(objectUrl);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    return true;
  } catch (error) {
    console.error('Failed to download file from base64', error);
    return false;
  }
};
export const SLOT_STATUS = ['Operational', 'Closed'];

export const copyContractDetails = contract => {
  const address = `${contract['propertyAddress']}, ${contract['propertyCity']}, ${contract['propertyState']} ${contract['zipCode']} (${contract['propertyCounty']})`;

  const inventoryCopyElems = [
    { label: 'Unit Location', key: 'unitLocation', objectKey: 'location' },
    { label: 'Manufacturer', key: 'manufacturer', objectKey: 'name' },
    { label: 'Model', key: 'model' },
    { label: 'Year', key: 'year' },
    { label: 'Serial #A', key: 'serialA' },
    { label: 'Serial #B', key: 'serialB' },
    { label: 'HUD #A', key: 'hudA' },
    { label: 'HUD #B', key: 'hudB' },
    { label: 'Size Overall', key: 'sizeOverall' }
  ];

  const copyElem = [
    { label: 'Sales Location', key: 'salesLocation', objectKey: 'location' },
    { label: 'Salesperson', key: 'salesperson' },
    { label: 'Buyer', key: 'buyer' },
    { label: 'Co-Buyer', key: 'coBuyer' },
    { label: 'Address', key: 'addr' },
    { label: 'Pindrop Url', key: 'pindropUrl' },
    { label: 'Cell Phone #', key: 'cellPhone' },
    { label: 'Cell Phone #2', key: 'cellPhone2' },
    { label: 'Buyer Email', key: 'buyerEmail' },
    ...inventoryCopyElems
  ]
    .map(({ label, key, objectKey }) => {
      if (key === 'addr') {
        return `Address: ${address}`;
      }

      if (inventoryCopyElems.map(e => e.label).includes(label)) {
        if (!contract['inventoryUnit']) return `${label}: N/A`;

        if (key === 'model') {
          return `${label}: ${contract['inventoryUnit']?.model?.modelName ||
            contract['inventoryUnit']?.model?.modelNumber ||
            'N/A'}`;
        }

        let value = contract['inventoryUnit'][key];
        if (value && typeof value === 'object') {
          value = value[objectKey];
        }
        return `${label}: ${value || 'N/A'}`;
      }

      let value = contract[key];
      if (value && typeof value === 'object') {
        value = value[objectKey];
      }

      return `${label}: ${value || 'N/A'}`;
    })
    .filter(item => !item.includes('N/A'))
    .join('\n');

  copyToClipboard(copyElem);
};

export const copyContractAddress = contract => {
  const address = `${contract['propertyAddress']}, ${contract['propertyCity']}, ${contract['propertyState']} ${contract['zipCode']}`;

  copyToClipboard(address);
};

export const copyContactDetails = (contact, inventoryUnit) => {
  const address = `${contact['propertyAddress']}, ${contact['propertyCity']}, ${contact['propertyState']} ${contact['zipCode']} (${contact['propertyCounty']})`;

  const inventoryCopyElems = [
    { label: 'Unit Location', key: 'unitLocation', objectKey: 'location' },
    { label: 'Manufacturer', key: 'manufacturer', objectKey: 'name' },
    { label: 'Model', key: 'model' },
    { label: 'Year', key: 'year' },
    { label: 'Serial #A', key: 'serialA' },
    { label: 'Serial #B', key: 'serialB' },
    { label: 'HUD #A', key: 'hudA' },
    { label: 'HUD #B', key: 'hudB' },
    { label: 'Size Overall', key: 'sizeOverall' }
  ];

  const copyFields = [
    { label: 'Sales Location', key: 'salesLocation', objectKey: 'location' },
    { label: 'Salesperson', key: 'salesperson' },
    { label: 'Buyer', key: 'buyer' },
    { label: 'Co-Buyer', key: 'coBuyer' },
    { label: 'Address', key: 'addr' },
    { label: 'Pindrop Url', key: 'pindropUrl' },
    { label: 'Cell Phone #', key: 'cellPhone' },
    { label: 'Cell Phone #2', key: 'cellPhone2' },
    { label: 'Buyer Email', key: 'buyerEmail' },
    ...inventoryCopyElems
  ];

  const inventoryKeys = new Set(inventoryCopyElems.map(e => e.key));

  const copyText = copyFields
    .map(({ label, key, objectKey }) => {
      if (key === 'addr') {
        return `Address: ${address}`;
      }

      if (inventoryKeys.has(key)) {
        if (!inventoryUnit) return null;

        if (key === 'model') {
          return `${label}: ${inventoryUnit['model']?.modelName || inventoryUnit['model']?.modelNumber || 'N/A'}`;
        }

        let value = inventoryUnit[key];
        if (value && typeof value === 'object') {
          value = value[objectKey];
        }
        return value ? `${label}: ${value}` : null;
      }

      let value = contact[key];
      if (value && typeof value === 'object') {
        value = value[objectKey];
      }
      return value ? `${label}: ${value}` : null;
    })
    .filter(Boolean)
    .join('\n');

  copyToClipboard(copyText);
};

export const copyContactAddress = contact => {
  const address = `${contact['propertyAddress']}, ${contact['propertyCity']}, ${contact['propertyState']} ${contact['zipCode']} (${contact['propertyCounty']})`;

  copyToClipboard(address);
};

export const copyServiceDetails = service => {
  const copyElem = [
    'homeManufacturer',
    'salesLocation',
    'salesperson',
    'name',
    'address',
    'phoneNumber',
    'emailAddress',
    'serviceIssue'
  ]
    .map(label => {
      return `${label}: ${service[label] || 'N/A'}`;
    })
    .filter(item => !item.includes('N/A'))
    .join('\n');

  copyToClipboard(copyElem);
};

export const copyInventoryDetails = contract => {
  const inventoryCopyElems = [
    { label: 'Unit Location', key: 'unitLocation', objectKey: 'location' },
    { label: 'Manufacturer', key: 'manufacturer', objectKey: 'name' },
    { label: 'Model', key: 'model' },
    { label: 'Year', key: 'year' },
    { label: 'Serial #A', key: 'serialA' },
    { label: 'Serial #B', key: 'serialB' },
    { label: 'HUD #A', key: 'hudA' },
    { label: 'HUD #B', key: 'hudB' },
    { label: 'Size Overall', key: 'sizeOverall' },
    { label: 'Invoice', key: 'invoice', preFix: '$' }
  ]
    .map(({ label, key, objectKey, preFix = '' }) => {
      if (key === 'model') {
        return `${label}: ${contract['model']?.modelName || contract['model']?.modelNumber || 'N/A'}`;
      }
      let value = contract[key];
      if (value && typeof value === 'object') {
        value = value[objectKey];
      }

      return `${label}: ${value || value === 0 ? `${preFix}${value}` : 'N/A'}`;
    })
    .filter(item => !item.includes('N/A'))
    .join('\n');

  copyToClipboard(inventoryCopyElems);
};

export const copyToClipboard = textToCopy => {
  // navigator clipboard api needs a secure context (https)
  if (navigator.clipboard && window.isSecureContext) {
    // navigator clipboard api method'
    return navigator.clipboard.writeText(textToCopy);
  } else {
    // text area method
    let textArea = document.createElement('textarea');
    textArea.value = textToCopy;
    // make the textarea out of viewport
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    return new Promise((res, rej) => {
      // here the magic happens
      document.execCommand('copy') ? res() : rej();
      textArea.remove();
    });
  }
};

export function isValidDate(d) {
  return d instanceof Date && !isNaN(d);
}

export function isISODateString(str) {
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;
  return typeof str === 'string' && isoRegex.test(str);
}

export const sortByDate = (a, b) => {
  if (!a || !isValidDate(a)) return 1;
  if (!b || !isValidDate(b)) return -1;
  return Number(b) - Number(a);
};

export const tableToCSV = table => {
  return table
    .map(row => {
      return row
        .map(col => {
          // Use the extracted function to check for ISO date string
          if (isISODateString(col)) {
            // Format the ISO string using moment
            return `"${moment(col).format('MMMM Do YYYY')}"`;
          }
          return `"${col}"`;
        })
        .join(',');
    })
    .join('\n');
};

export const getValidUrl = link => {
  if (!link.startsWith('https://') && !link.startsWith('http://')) {
    link = 'https://' + link;
  }

  return link;
};

export const openLinkInNewTab = (link, absolute = false) => {
  if (absolute && !link.startsWith('https://') && !link.startsWith('http://')) {
    link = 'https://' + link;
  }
  window.open(absolute ? link : `${APP_URL}${link}`, '_blank');
};

export const getDocIdFromUrl = (url = '') => url.match(/[-\w]{25,}/)?.[0];

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
export const humanFileSize = (bytes, si = false, dp = 1) => {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(dp) + ' ' + units[u];
};

export const getRedirectinLinkForFields = field => {
  if (field === 'contactResults') return '/pipeline/crm?q=';
  if (field === 'inventoryResults') return '/inventory/browse?q=';
  if (field === 'contractResults') return '/contract/search?q=';
};

// Function to group entities into their respective groups
export const groupEntityTableValues = (data, groupBy, groupValues) => {
  if (!groupBy || !groupValues) return [...data];
  // Initialize an object to store grouped items
  const groupedItems = [...groupValues, 'Others'].reduce((obj, group) => {
    return { ...obj, [group]: [] };
  }, {});

  // Iterate over each item in the data array
  data.forEach(item => {
    if (groupBy === 'roles' && Array.isArray(item.roles) && item.roles.length > 0) {
      const matchingRoles = item.roles.filter(r => groupValues.includes(r));
      if (matchingRoles.length > 0) {
        matchingRoles.forEach(r => {
          groupedItems[r].push({
            ...item,
            __rowKey: `${item._id}-${r}`,
            groupSectionRole: r
          });
        });
      } else {
        groupedItems['Others'].push({ ...item });
      }
      return;
    }

    const groupValue = item[groupBy];

    if (groupValues.includes(groupValue)) {
      groupedItems[groupValue].push({ ...item });
    } else {
      groupedItems['Others'].push({ ...item });
    }
  });

  let finalArray = [];
  for (const group of [...groupValues, 'Others']) {
    if (groupedItems[group]?.length > 0) {
      finalArray.push({ _id: group, content: group, isHeader: true }, ...groupedItems[group]);
    }
  }

  return finalArray;
};

export const findSpecificSectionVideo = (videoUrls = [], route) => {
  return videoUrls.find(
    paramRoute => paramRoute?.name !== 'Featured Videos' && getRegexForPath(paramRoute?.path).test(route)
  );
};

export const matchIdParameter = route => {
  return /[a-f\d]{24}/.test(route);
};

export const getRegexForPath = path => {
  const routeRegEx = new RegExp(path);
  return routeRegEx;
};

export const parseHTML = htmlString => {
  const tempElement = document.createElement('div');
  tempElement.innerHTML = htmlString;
  return tempElement.firstChild;
};

export const sortEntityItemsAccordingToPositions = items => {
  return items.sort((item1, item2) => Number(item1?.position) - Number(item2?.position));
};

export const fillSizesInInventory = (overallSizeOfInventory, sections) => {
  const pattern = /^\d+x\d+$/;
  const numberOfSections = sections || Number(document.getElementById('numberOfSections')?.value);
  const overallSize = document.getElementById('sizeOverall')?.value || overallSizeOfInventory || '';

  let allBValues = ['sizeB', 'serialB', 'hudB'];
  let allAValues = ['sizeA', 'serialA', 'hudA'];
  let allCValues = ['sizeC', 'serialC', 'hudC'];

  [...allAValues, ...allBValues, ...allCValues].forEach(id => {
    const column = document.getElementById(`${id}-col-container`);

    if (column) {
      column.style.display = 'block';
    }
  });

  if (numberOfSections) {
    [...(numberOfSections === 1 ? allBValues : []), ...(numberOfSections <= 2 ? allCValues : [])].forEach(id => {
      const colToBeRemoved = document.getElementById(`${id}-col-container`);

      if (colToBeRemoved) {
        colToBeRemoved.style.display = 'none';
      }
    });
  }

  const splittedOverallSize = overallSize.split('x');
  const leftHandOfOverallSize = parseInt(Number(splittedOverallSize[0]) / numberOfSections);

  const elementA = document.getElementById('sizeA');
  const elementB = document.getElementById('sizeB');
  const elementC = document.getElementById('sizeC');

  const allElements = [elementA, elementB, elementC];
  if (pattern.test(overallSize)) {
    if (isNaN(leftHandOfOverallSize) || leftHandOfOverallSize === 0) {
      allElements.forEach(element => {
        if (element) {
          element.value = '';
        }
      });
    } else {
      allElements.forEach(element => {
        if (element) {
          element.value = `${leftHandOfOverallSize}x${splittedOverallSize[1]}`;
        }
      });
    }
  } else {
    allElements.forEach(element => {
      if (element) {
        element.value = '';
      }
    });
  }
};

export const updateSizesAccordingToSections = (overallSize = '', numberOfSections = 1) => {
  const splittedOverallSize = overallSize.split('x');
  const leftOfOverallSize = splittedOverallSize[0];
  const rightOfOverallSize = splittedOverallSize[1];

  if (!leftOfOverallSize || !rightOfOverallSize) {
    return {
      sizeA: '',
      sizeB: '',
      sizeC: ''
    };
  }

  const sizePerSection = parseInt(Number(leftOfOverallSize) / Number(numberOfSections));
  if (isNaN(sizePerSection) || sizePerSection === 0) {
    return {
      sizeA: '',
      sizeB: '',
      sizeC: ''
    };
  }

  return {
    sizeA: `${sizePerSection}x${rightOfOverallSize}`,
    sizeB: numberOfSections > 1 ? `${sizePerSection}x${rightOfOverallSize}` : '',
    sizeC: numberOfSections > 2 ? `${sizePerSection}x${rightOfOverallSize}` : ''
  };
};

export const updateColumnsAccordingToSections = numberOfSections => {
  if (!numberOfSections || numberOfSections === 1 || numberOfSections === 3) {
    return 4;
  }
  if (numberOfSections === 2) {
    return 6;
  }

  return 6;
};

export const isObjectIncomplete = (object, pathToCompare = []) => {
  return !object || Object.keys(object).length <= pathToCompare.length + 5; //allowing some slack;
};

export const splitFullName = (fullName = '') => {
  if (!fullName) {
    return '';
  }

  const names = fullName.trim().split(' ');

  if (names.length === 1) {
    return names[0];
  } else {
    return names.slice(1).join(' ') + ', ' + names[0];
  }
};

export const splitPropertyInfo = (propertyAddress, propertyCity) => {
  if (!propertyAddress && !propertyCity) {
    return ' - ';
  }

  if (!propertyAddress) {
    return propertyCity;
  }

  if (!propertyCity) {
    return propertyAddress;
  }

  return `${propertyAddress}, ${propertyCity}`;
};

export function handleKeyDownDefault(cell, keyCode, ctrl, shift, alt) {
  if (!ctrl && !alt && isAlphaNumericKey(keyCode)) {
    return { cell, enableEditMode: false };
  }

  return {
    cell,
    enableEditMode: keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER
  };
}

export function formatInventoryUnit(inventoryUnit) {
  if (!inventoryUnit) return ' - / - / - ';

  const getValue = (...keys) => {
    for (const key of keys) {
      const keyParts = key.split('.');
      let value = inventoryUnit;

      for (const part of keyParts) {
        if (!value) break;
        value = value[part];
      }

      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return ' - ';
  };

  const manufacturer = getValue('manufacturer.name', 'manufacturer');

  const manufacturerName = [manufacturer].filter(val => val && val !== 'N/A').join(' ') || 'Not specified';

  const model = getValue('model.modelName', 'model.modelNumber');
  const sizeOverall = getValue('sizeOverall');

  return `${manufacturerName} / ${model} / ${sizeOverall}`;
}

export const formatLenderAndLoneTypeInfo = contact => {
  if (!contact?.loanType) {
    return contact?.lender?.shortName;
  }
  if (!contact?.lender) {
    return contact?.loanType;
  }
  if (contact?.loanType && contact?.lender) {
    return `${contact?.lender?.shortName} / ${contact?.loanType}`;
  }
};

export const getPnlValue = (record, key, appChoices) => {
  const inventoryUnit = getCommissionInventoryUnit(record);
  const value = inventoryUnit?.pnlOptions?.find(o => o.key === key)?.value;
  let pnlValue;

  if (value != null && value !== undefined) {
    pnlValue = Number(value);
  } else {
    const { pnlOptions } = getPnlConfigForInventory(
      appChoices,
      inventoryUnit?.unitLocation?._id || inventoryUnit?.unitLocation,
      inventoryUnit?.numberOfSections
    );
    const defaultValue = pnlOptions?.find(o => o.name === key)?.defaultValue;
    pnlValue = defaultValue != null ? Number(defaultValue) : 0;
  }

  return isNaN(pnlValue) ? 0 : pnlValue;
};

export const calculateRebate = record => {
  const inventoryUnit = getCommissionInventoryUnit(record);

  if (inventoryUnit?.pnlRebate !== null && inventoryUnit?.pnlRebate !== undefined) {
    return inventoryUnit.pnlRebate;
  }

  const val = 0.05 * (Number(inventoryUnit?.basePrice || 0) + Number(inventoryUnit?.options || 0));
  return val.toFixed(2);
};

export const calculateTotalGP = (contract, appChoices, expenseFields, incomeFields) => {
  const reduceTotalValue = (prev, { key, valueGetter, isPnLOption }) => {
    let value;
    if (isPnLOption) {
      value = getPnlValue(contract, key, appChoices);
    } else {
      value = (valueGetter ? valueGetter(contract) : contract[key]) || 0;
    }

    return prev + Number(value);
  };

  const totalGPMinuend = expenseFields.reduce(reduceTotalValue, 0);
  const totalGPSubtrahend = incomeFields.reduce(reduceTotalValue, 0);

  return totalGPMinuend - totalGPSubtrahend;
};

export const getCommissionInventoryUnit = record => {
  if (!record?.inventoryUnit) return null;
  if (record.newStatus === 'Contracted' || !Array.isArray(record.inventoryUnit)) {
    return record.inventoryUnit;
  }
  return record.inventoryUnit[0];
};

export const sumCommissionOptionValues = (options = []) =>
  (options || []).reduce((total, option) => total + Number(option?.value ?? 0), 0);

export const calculateCommissionDue = c => {
  const inventoryUnit = getCommissionInventoryUnit(c);
  const rawInvoiceValue = inventoryUnit?.invoice;
  const inventoryInvoice = Number.isFinite(rawInvoiceValue) ? Number(rawInvoiceValue) : 0;
  const salesPrice = Number(c?.salesPrice || 0);
  const commissionLessValue = sumCommissionOptionValues(inventoryUnit?.commissionSheetLessOptions);
  const netCommissionsTotal = salesPrice - commissionLessValue;
  const optionsTotal = inventoryInvoice + sumCommissionOptionValues(inventoryUnit?.commissionSheetOptions);
  const grossProfit = netCommissionsTotal - optionsTotal;

  let commissionDue;

  if (isMicasa) {
    commissionDue = grossProfit >= 20000 ? 0.25 * grossProfit : 0.2 * grossProfit;
  } else {
    commissionDue = grossProfit / 2;
  }

  const curtailmentBonus = Number(c?.additionalFields?.curtailmentBonus || 0);
  const teamLeaderBonus = Number(c?.additionalFields?.teamLeaderBonus || 0);

  const commissionDueWithBonus = commissionDue + curtailmentBonus + teamLeaderBonus;
  return commissionDueWithBonus;
};

export const getDateFromInputString = dateString => {
  try {
    return new Date(dateString.split('-').join('/')).toISOString();
  } catch (e) {
    return null;
  }
};

export const normalizeBoxSize = size => {
  const [a, b] = String(size || '')
    .split('x')
    .map(s => s.trim());
  if (!a || !b || isNaN(a) || isNaN(b)) return null;
  return `${Number(a)}x${Number(b)}`;
};
